/**
 * PretextedAccordion — extends Radix Accordion with pretext-style height animation.
 *
 * The core idea (from the @chenglou/pretext accordion demo):
 *
 *   .accordion-body { height: 0; overflow: clip; transition: height 180ms ease }
 *   body.style.height = expanded ? `${calculatedPx}px` : '0px'
 *
 * Radix's shadcn wrapper instead freezes height via --radix-accordion-content-height
 * (measured once via scrollHeight) and applies it as `h-(--radix-accordion-content-height)`.
 * When nested accordions change content size, that CSS variable goes stale.
 *
 * This component replaces that with:
 *   1. forceMount — content stays in the DOM at all times (always fully rendered)
 *   2. ResizeObserver — tracks the inner div's natural offsetHeight continuously
 *   3. MutationObserver — watches Radix's data-state attribute for open/closed
 *   4. Inline style — drives height: 0 ↔ ${measured}px directly, no CSS variable
 *
 * Usage (drop-in replacement for shadcn Accordion):
 *
 *   import {
 *     PretextedAccordion,
 *     PretextedAccordionItem,
 *     PretextedAccordionTrigger,
 *     PretextedAccordionContent,
 *   } from '@/components/common/pretexted-accordion'
 *
 *   <PretextedAccordion type="multiple">
 *     <PretextedAccordionItem value="item-1">
 *       <PretextedAccordionTrigger>Title</PretextedAccordionTrigger>
 *       <PretextedAccordionContent>Any content</PretextedAccordionContent>
 *     </PretextedAccordionItem>
 *   </PretextedAccordion>
 *
 * For custom triggers (e.g. Button asChild), use AccordionPrimitive.Trigger directly
 * alongside PretextedAccordionItem + PretextedAccordionContent.
 */

import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { Accordion as AccordionPrimitive } from 'radix-ui'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon, ArrowUp01Icon } from '@hugeicons/core-free-icons'

import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export const PretextedAccordion = forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Root
    ref={ref}
    data-slot="accordion"
    className={cn('flex w-full flex-col', className)}
    {...props}
  />
))
PretextedAccordion.displayName = 'PretextedAccordion'

// ---------------------------------------------------------------------------
// Item
// ---------------------------------------------------------------------------

export const PretextedAccordionItem = forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-slot="accordion-item"
    className={cn('not-last:border-b', className)}
    {...props}
  />
))
PretextedAccordionItem.displayName = 'PretextedAccordionItem'

// ---------------------------------------------------------------------------
// Trigger (standard chevron style — mirrors shadcn AccordionTrigger)
// For custom triggers (Button asChild), use AccordionPrimitive.Trigger directly.
// ---------------------------------------------------------------------------

export const PretextedAccordionTrigger = forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ children, className, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      data-slot="accordion-trigger"
      className={cn(
        'group/accordion-trigger relative flex flex-1 items-start justify-between rounded-md border border-transparent py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 **:data-[slot=accordion-trigger-icon]:ml-auto **:data-[slot=accordion-trigger-icon]:size-4 **:data-[slot=accordion-trigger-icon]:text-muted-foreground',
        className,
      )}
      {...props}
    >
      {children}
      <HugeiconsIcon
        icon={ArrowDown01Icon}
        strokeWidth={2}
        data-slot="accordion-trigger-icon"
        className="pointer-events-none shrink-0 group-aria-expanded/accordion-trigger:hidden"
      />
      <HugeiconsIcon
        icon={ArrowUp01Icon}
        strokeWidth={2}
        data-slot="accordion-trigger-icon"
        className="pointer-events-none hidden shrink-0 group-aria-expanded/accordion-trigger:inline"
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
PretextedAccordionTrigger.displayName = 'PretextedAccordionTrigger'

// ---------------------------------------------------------------------------
// Content — the pretext-style height driver
// ---------------------------------------------------------------------------

export const PretextedAccordionContent = forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    /**
     * className applied to the inner padding div (the direct parent of children).
     * Defaults to 'pb-4 pt-0' to match shadcn AccordionContent.
     */
    innerClassName?: string
  }
>(({ children, className, innerClassName, ...props }, forwardedRef) => {
  const elemRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)
  const [isOpen, setIsOpen] = useState(false)

  // Merge the forwarded ref with our local ref so callers can still access the element
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      ;(elemRef as React.MutableRefObject<HTMLDivElement | null>).current = el
      if (typeof forwardedRef === 'function') forwardedRef(el)
      else if (forwardedRef)
        (
          forwardedRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = el
    },
    [forwardedRef],
  )

  // useLayoutEffect — runs before paint, so on initial render the height is correct
  // without a visible flash (analogous to the pretext demo reading height before draw).
  useLayoutEffect(() => {
    const content = elemRef.current
    const inner = innerRef.current
    if (!content || !inner) return

    // Initial read
    setIsOpen(content.dataset['state'] === 'open')
    setHeight(inner.offsetHeight)

    // ResizeObserver — mirrors the pretext demo's window resize handler:
    // recalculate height whenever the content changes size (responsive layout, dynamic content)
    const resizeObs = new ResizeObserver(() => setHeight(inner.offsetHeight))
    resizeObs.observe(inner)

    // MutationObserver — watches Radix's data-state attribute to know open/closed
    const mutationObs = new MutationObserver(() =>
      setIsOpen(content.dataset['state'] === 'open'),
    )
    mutationObs.observe(content, {
      attributes: true,
      attributeFilter: ['data-state'],
    })

    return () => {
      resizeObs.disconnect()
      mutationObs.disconnect()
    }
  }, [])

  return (
    <AccordionPrimitive.Content
      ref={setRef}
      // forceMount keeps children in the DOM at all times so ResizeObserver can
      // continuously track their natural height — the same reason the pretext demo
      // always renders all panel text before measuring.
      forceMount
      data-slot="accordion-content"
      className={cn('text-sm', className)}
      style={{
        // Mirrors .accordion-body { height: 0; overflow: clip; transition: height 180ms ease }
        height: isOpen ? `${height}px` : '0px',
        overflow: 'clip',
        transition: 'height 180ms ease',
      }}
      {...props}
    >
      <div ref={innerRef} className={cn('pb-4 pt-0', innerClassName)}>
        {children}
      </div>
    </AccordionPrimitive.Content>
  )
})
PretextedAccordionContent.displayName = 'PretextedAccordionContent'
